"use client";

import { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import {
  isExternalLink,
  isInternalNextLink,
  isMissingProtocol,
} from "../../utils/SchoolBlocksUtilities";
import { useStores } from "../../stores/useStores";
import type { AnalyticsEventName } from "../../context/AnalyticsContext";
import AnalyticsEvent from "./AnalyticsEvent";
import type { UrlObject } from "url";

type HrefValue = string | UrlObject | null;

export interface ClickableLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> {
  href?: HrefValue;
  as?: string;
  nofollow?: boolean;
  scroll?: boolean;
  replace?: boolean;
  shallow?: boolean;
  prependLocale?: boolean;
  children?: ReactNode;
  analyticsEvent?: AnalyticsEventName;
  analyticsProperties?: Record<string, unknown>;
}

const ClickableLink = observer(function ClickableLink(
  props: ClickableLinkProps,
) {
  const {
    nofollow = true,
    children,
    scroll = true,
    replace = false,
    as,
    shallow = false,
    prependLocale = true,
    analyticsEvent,
    analyticsProperties,
    onClick,
    href: hrefProp,
    ...validDomAttributes
  } = props;

  const { interfaceStore, i18nStore } = useStores();

  // Compute the href string for analytics enrichment
  const linkHrefForAnalytics =
    typeof hrefProp === "string" ? hrefProp : (hrefProp?.pathname ?? undefined);

  const noUrl = hrefProp === undefined || hrefProp === null || hrefProp === "";

  if (noUrl) {
    let childrenArr: ReactNode[] = children
      ? Array.isArray(children)
        ? children
        : [children]
      : [];

    switch (childrenArr.length) {
      case 0:
        return null;
      case 1:
        return (
          <AnalyticsEvent
            as="a"
            {...validDomAttributes}
            href="#"
            analyticsEvent={analyticsEvent}
            analyticsProperties={{
              ...analyticsProperties,
              link_href: linkHrefForAnalytics,
            }}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.preventDefault();
              onClick?.(e as MouseEvent<HTMLAnchorElement>);
            }}
          >
            {childrenArr[0]}
          </AnalyticsEvent>
        );
      default:
        return (
          <span
            {...(validDomAttributes as React.HTMLAttributes<HTMLSpanElement>)}
          >
            {childrenArr}
          </span>
        );
    }
  }

  const href = hrefProp;

  if (typeof href === "object") {
    return (
      <Link
        href={href}
        as={as}
        scroll={scroll}
        replace={replace}
        shallow={shallow}
        locale={prependLocale ? i18nStore?.currentOrgSupportedLocale : false}
        prefetch={false}
        passHref
        legacyBehavior
      >
        <AnalyticsEvent
          as="a"
          {...validDomAttributes}
          analyticsEvent={analyticsEvent}
          analyticsProperties={{
            ...analyticsProperties,
            link_href: linkHrefForAnalytics,
          }}
          onClick={
            onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined
          }
        >
          {children}
        </AnalyticsEvent>
      </Link>
    );
  }

  if (isInternalNextLink(href, interfaceStore?.currentFullUrl)) {
    return (
      <Link
        href={href}
        as={as || href}
        scroll={scroll}
        replace={replace}
        shallow={shallow}
        locale={prependLocale ? i18nStore?.currentOrgSupportedLocale : false}
        prefetch={false}
        passHref
        legacyBehavior
      >
        <AnalyticsEvent
          as="a"
          {...validDomAttributes}
          analyticsEvent={analyticsEvent}
          analyticsProperties={{
            ...analyticsProperties,
            link_href: linkHrefForAnalytics,
          }}
          onClick={
            onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined
          }
        >
          {children}
        </AnalyticsEvent>
      </Link>
    );
  }

  let finalHref = href;
  if (isMissingProtocol(finalHref)) {
    finalHref = "https://" + finalHref;
  }

  const externalProps = { ...validDomAttributes };

  if (isExternalLink(finalHref, interfaceStore.currentFullUrl)) {
    externalProps.target = "_blank";
    externalProps.rel = `${nofollow ? "nofollow" : ""} noopener noreferrer`;
  }

  return (
    <AnalyticsEvent
      as="a"
      {...externalProps}
      href={finalHref}
      analyticsEvent={analyticsEvent}
      analyticsProperties={analyticsProperties}
      onClick={onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined}
    >
      {children}
    </AnalyticsEvent>
  );
});

export default ClickableLink;
